import {useCallback} from 'react';
import {useDispatch} from 'react-redux';

import {useCachedTable} from 'src/hooks/common-data-service/cached-table';
import {getActivePlatformAlerts} from 'src/openapi/admin/apis/PlatformAlertsApi';
import {useAppDispatch} from 'src/store/hooks';

import {useAbortSignal} from '@pb/kit-hooks-use-abort-signal';

import {SORT_MAP} from '../constants';
import {activePlatformAlertsStateSelector} from '../slices/active-platform-alerts-state.slice';
import {
    activePlatformAlertsActions,
    activePlatformAlertsDataSelector,
    activePlatformAlertsResultSelector,
} from '../slices/active-platform-alerts.slice';

import type {ActivePlatformAlertsFilters, ActivePlatformAlertsSortField} from '../typedef';
import type {CacheKeys} from 'src/hooks/common-data-service/cached-table';
import type {AlertType} from 'src/openapi/admin/models/AlertType';
import type {PlatformAlert} from 'src/openapi/admin/models/PlatformAlert';


type ActivePlatformAlertsCacheKeys = CacheKeys<undefined, ActivePlatformAlertsSortField, ActivePlatformAlertsFilters>;

export const useGetActivePlatformAlerts = () => {
    const apiDispatch = useAppDispatch();
    const dispatch = useDispatch();
    const {createAbortSignal} = useAbortSignal();

    const getAction = useCallback(() => apiDispatch(
        getActivePlatformAlerts({signal: createAbortSignal()}),
    ), [apiDispatch, createAbortSignal]);

    const setLoadedData = useCallback((
        response: Array<PlatformAlert>,
        cacheKeys: ActivePlatformAlertsCacheKeys,
    ) => {
        const {pageSize, currentPage} = cacheKeys.pagination;
        const sortComparator = SORT_MAP[cacheKeys.sortCriteria.field];

        const data = response
            .filter(alert => {
                if (cacheKeys.filters.message) {
                    const sanitizedMessage = alert.message.toLowerCase().trim();
                    const sanitizedFilter = (cacheKeys.filters.message as string).toLowerCase().trim();
                    return sanitizedMessage.includes(sanitizedFilter);
                }
                return true;

            })
            .filter(alert => {
                if (cacheKeys.filters.type) {
                    return (cacheKeys.filters.type as AlertType[]).includes(alert.alertType);
                }
                return true;
            })
            .sort((a, b) => sortComparator(a, b, cacheKeys.sortCriteria.direction));

        const paginatedData = data.slice((currentPage - 1) * pageSize, currentPage * pageSize);

        dispatch(activePlatformAlertsActions.onSuccess({
            cacheKeys,
            totalCount: data.length,
            data: paginatedData,
        }));
    }, [dispatch]);

    return useCachedTable({
        dataSelector: activePlatformAlertsDataSelector,
        stateSelector: activePlatformAlertsStateSelector,
        resultSelector: activePlatformAlertsResultSelector,
        actions: activePlatformAlertsActions,
        getAction,
        onSuccessLoad: setLoadedData,
    });
};
